<template>
    <div class="nk-app-root sip-shell sip-shell--clean">
        <div class="nk-main sip-shell__grid">
            <div
                class="sip-nav-overlay"
                :class="{ 'is-open': sidebarOpen }"
                @click="closeSidebar"
            ></div>

            <app-sidebar
                :is-open="sidebarOpen"
                :nav-items="visibleMenu"
                :nav-metrics="navMetrics"
                :logo-url="headerLogoUrl"
                :instance-stats="instance_stats"
                @close-sidebar="closeSidebar"
            ></app-sidebar>

            <div class="nk-wrap sip-workspace">
                <header class="sip-topbar">
                    <div class="container-fluid sip-topbar__container">
                        <div class="sip-topbar__row sip-topbar__row--primary">
                            <div class="sip-topbar__brand-wrap d-xl-none">
                                <a
                                    href="#"
                                    class="sip-topbar__menu-btn"
                                    role="button"
                                    aria-controls="MainSideBarRightMenu"
                                    :aria-expanded="sidebarOpen ? 'true' : 'false'"
                                    aria-label="Open menu"
                                    @click.prevent="openSidebar"
                                >
                                    <span class="sip-mobile-menu" aria-hidden="true">
                                        <span></span>
                                        <span></span>
                                    </span>
                                </a>

                                <router-link to="/" class="sip-topbar__brand">
                                    <span class="sip-topbar__mark">
                                        <img
                                            v-if="headerLogoUrl && !headerLogoError"
                                            :src="headerLogoUrl"
                                            alt=""
                                            @error="headerLogoError = true"
                                        />
                                        <span v-else>S</span>
                                    </span>
                                    <span class="sip-topbar__brand-copy">
                                        <strong>Sipinor</strong>
                                    </span>
                                </router-link>
                            </div>

                            <div class="sip-topbar__search">
                                <admin-global-search />
                            </div>

                            <div class="sip-topbar__actions">
                                <button
                                    type="button"
                                    class="sip-topbar__icon-btn"
                                    :title="isDarkMode ? 'Light mode' : 'Dark mode'"
                                    :aria-label="isDarkMode ? 'Light mode' : 'Dark mode'"
                                    @click="toggleDarkMode"
                                >
                                    <admin-icon :name="isDarkMode ? 'sun' : 'moon'" aria-hidden="true"></admin-icon>
                                </button>

                                <router-link
                                    v-if="hasPermission('list-query')"
                                    to="/query"
                                    class="sip-topbar__icon-btn"
                                    title="Queries"
                                    aria-label="Queries"
                                >
                                    <admin-icon name="bell" aria-hidden="true"></admin-icon>
                                    <small v-if="query_stats.total" class="sip-topbar__badge">{{ query_stats.total }}</small>
                                </router-link>

                                <div class="dropdown user-dropdown sip-topbar__user">
                                    <a
                                        href="#"
                                        ref="profileMenuButton"
                                        class="sip-topbar__user-btn dropdown-toggle"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-label="Open profile menu"
                                        @keydown.down.prevent="focusFirstProfileItem"
                                    >
                                        <span class="sip-topbar__avatar">{{ headerInitial }}</span>
                                        <span class="sip-topbar__user-meta d-none d-md-inline">{{ headerFirstName }}</span>
                                        <admin-icon name="chevron-down" class="sip-topbar__caret d-none d-md-inline" aria-hidden="true"></admin-icon>
                                    </a>
                                    <div class="dropdown-menu dropdown-menu-end user-dropdown-menu sip-dropdown" role="menu" @keydown.esc.prevent="closeProfileMenu">
                                        <div class="dropdown-inner">
                                            <div class="sip-dropdown__head">
                                                <span class="sip-dropdown__avatar" aria-hidden="true">{{ headerInitial }}</span>
                                                <span class="sip-dropdown__copy">
                                                    <span class="sip-dropdown__name">{{ headerFullName }}</span>
                                                    <span class="sip-dropdown__email">{{ headerEmail }}</span>
                                                </span>
                                            </div>
                                            <ul class="link-list link-list-opt no-bdr sip-dropdown__menu">
                                                <li>
                                                    <router-link to="/profile" role="menuitem">
                                                        <admin-icon name="user"></admin-icon>
                                                        <span>{{ trans('user.profile') }}</span>
                                                    </router-link>
                                                </li>
                                                <li>
                                                    <router-link to="/change-password" role="menuitem">
                                                        <admin-icon name="lock"></admin-icon>
                                                        <span>{{trans('user.change_password')}}</span>
                                                    </router-link>
                                                </li>
                                                <li class="sip-dropdown__logout">
                                                    <a href="#" role="menuitem" @click.prevent="logout">
                                                        <admin-icon name="logout"></admin-icon>
                                                        <span>{{trans('auth.logout')}}</span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <main class="nk-content nk-content-fluid sip-page-shell">
                    <div class="container-fluid sip-page-shell__container">
                        <div class="nk-content-body">
                            <router-view :key="$route.fullPath" />
                        </div>
                    </div>
                </main>

                <footer class="nk-footer sip-footer">
                    <div class="container-fluid">
                        <div class="nk-footer-wrap">
                            <app-footer></app-footer>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
    import AppSidebar from './sidebar.vue'
    import AppFooter from './footer.vue'
    import { buildAdminMenuItems, isAdminNavActive } from './admin-menu.js'

    export default {
        data(){
            return {
                isDarkMode: document.body.classList.contains('dark-mode'),
                sidebarOpen: false,
                headerLogoError: false,
                instance_stats: {
                    total: 0,
                    running: 0,
                    expired: 0,
                    suspended: 0,
                    terminated: 0,
                },
                query_stats: {
                    total: 0,
                },
                subscriber_stats: {
                    subscribed: 0,
                    unsubscribed: 0,
                },
            }
        },
        watch: {
            '$route.fullPath'() {
                this.closeSidebar();
            },
            headerLogoUrl() {
                this.headerLogoError = false;
            },
        },
        computed: {
            headerFullName() {
                const n = (helper.getAuthUser('full_name') || '').trim();
                return n || this.trans('user.profile');
            },
            headerEmail() {
                return helper.getAuthUser('email') || '';
            },
            headerInitial() {
                const name = this.headerFullName || 'A';
                return name.charAt(0).toUpperCase();
            },
            headerFirstName() {
                const parts = this.headerFullName.split(/\s+/).filter(Boolean);
                return parts[0] || this.headerFullName;
            },
            headerLogoUrl() {
                const p = this.$store.getters.getConfig('sidebar_logo') || this.$store.getters.getConfig('main_logo');
                if (p === null || p === undefined || String(p).trim() === '') {
                    return '';
                }
                const s = String(p).trim();
                if (/^https?:\/\//i.test(s)) {
                    return s;
                }
                return '/' + s.replace(/^\//, '');
            },
            visibleMenu() {
                return buildAdminMenuItems(this).filter((item) => !item.perm || this.hasPermission(item.perm));
            },
            navMetrics() {
                const metrics = {};
                if (this.instance_stats.total) {
                    metrics.instances = this.instance_stats.total;
                }
                if (this.query_stats.total) {
                    metrics.queries = this.query_stats.total;
                }
                const subscribers = Number(this.subscriber_stats.subscribed) + Number(this.subscriber_stats.unsubscribed);
                if (subscribers) {
                    metrics.subscribers = subscribers;
                }
                return metrics;
            },
        },
        methods : {
            hasPermission(permission) {
                return helper.hasPermission(permission);
            },
            isActive(item) {
                return isAdminNavActive(this.$route.path, item);
            },
            loadShellStats() {
                if (!this.hasPermission('list-instance') && !this.hasPermission('list-query')) {
                    return;
                }
                axios.get('/api/dashboard')
                    .then(response => response.data)
                    .then(response => {
                        this.instance_stats = Object.assign({}, this.instance_stats, response.instance_stats || {});
                        this.query_stats = Object.assign({}, this.query_stats, response.query_stats || {});
                        this.subscriber_stats = Object.assign({}, this.subscriber_stats, response.subscriber_stats || {});
                    })
                    .catch(() => {});
            },
            openSidebar() {
                this.sidebarOpen = true;
            },
            closeSidebar() {
                this.sidebarOpen = false;
            },
            onShellKeydown(e) {
                if (e.key === 'Escape' && this.sidebarOpen) {
                    this.closeSidebar();
                }
            },
            focusFirstProfileItem() {
                $('.sip-topbar__user-btn').dropdown('show');
                this.$nextTick(() => {
                    const menu = this.$el.querySelector('.sip-dropdown');
                    const first = menu && menu.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
                    if (first) {
                        first.focus();
                    }
                });
            },
            closeProfileMenu() {
                $('.sip-topbar__user-btn').dropdown('hide');
                if (this.$refs.profileMenuButton) {
                    this.$refs.profileMenuButton.focus();
                }
            },
            toggleDarkMode() {
                document.body.classList.toggle('dark-mode');
                this.isDarkMode = document.body.classList.contains('dark-mode');
                try {
                    localStorage.setItem('dark-mode', this.isDarkMode ? '1' : '0');
                } catch (e) {}
            },
            logout() {
                helper.logout().then(() => {
                    this.$store.dispatch('resetAuthUserDetail');
                    this.$router.push('/login');
                });
            }
        },
        components: {
            AppSidebar, AppFooter
        },
        mounted() {
            helper.notification();
            this.loadShellStats();

            try {
                const saved = localStorage.getItem('dark-mode');
                if (saved === '1') {
                    document.body.classList.add('dark-mode');
                    this.isDarkMode = true;
                } else if (saved === '0') {
                    document.body.classList.remove('dark-mode');
                    this.isDarkMode = false;
                }
            } catch (e) {}

            $('body').addClass("fix-header card-no-border sip-clean-shell");
            $('body').removeClass("fix-sidebar");
            $("body").trigger("resize");
            document.addEventListener('keydown', this.onShellKeydown);
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.onShellKeydown);
            $('body').removeClass("sip-clean-shell");
        },
    }
</script>
